import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import Box from '@mui/joy/Box';
import ListItem from '@mui/joy/ListItem';
import Divider from '@mui/joy/Divider';
import List from '@mui/joy/List';
import Typography from '@mui/joy/Typography';
import { useAuth } from '../../../hooks/useAuth';
import { isProd } from '../../../utils/env';
import { version } from '../../../../package.json';
import BillingsIcon from '../../../assets/images/billings-icon.svg?react';
import ArrowIcon from '../../../assets/images/sidebar-arrow-dropdown.svg?react';
import AdminDashboardIcon from '../../../assets/images/admin-dashboard-icon.svg?react';
import UsersManagementIcon from '../../../assets/images/users-managment-icon.svg?react';
import GroupsManagmentIcon from '../../../assets/images/groups-managment-icon.svg?react';
import HealthIcon from '../../../assets/images/health-icon.svg?react';
import SingleReportIcon from '../../../assets/images/single-report-icon.svg?react';
import BulkTestIcon from '../../../assets/images/bulk-tests-icon.svg?react';
import DocumentationIcon from '../../../assets/images/documentation-icon.svg?react';
import BellIcon from '../../../assets/images/utility-icons/bell.svg?react';
import DashboardIcon from '../../../assets/images/dashboard-icon.svg?react';
import CollapseIcon from '../../../assets/images/collapse-icon.svg?react';
import RobotIcon from '../../../assets/images/utility-icons/robot.svg?react';
import LogoLarge from '../../../assets/images/athena-logo-desktop.png';
import LogoSmall from '../../../assets/images/athena-logo-small.png';
import { NotificationCategories } from '../../../interfaces/notification.interface';
import { useGetDocumentationPageNamesQuery } from '../../../services/api.documentation';
import useCanExtractData from '../../../hooks/useCanExtractData';
import useCanModifyEmbargoKeyword from '../../../hooks/useCanModifyEmbargoKeyword';
import useCanModifyUrlExceptions from '../../../hooks/useCanModifyUrlExceptions';
import useCanModifySkuChecker from '../../../hooks/useCanModifySkuChecker';
import './Sidebar.css';
import useCanSeeBlockedUsers from '../../../hooks/useCanSeeBlockedUsers';

const documentationOptionsBase = [
  { url: 'Status-Codes', title: 'Status Codes' },
  { url: 'Health-Score', title: 'Health Score' }
];

const generalNotificationOptions: SidebarDropdownOption[] = [
  { url: 'all', title: 'All Notifications' },
  { url: NotificationCategories.MaintenanceNotice, title: 'Maintenance Notice' },
  { url: NotificationCategories.ReleaseNotes, title: 'Release Notes' },
  { url: NotificationCategories.GeneralInformation, title: 'General Information' }
];

const blockedUsersNotificationsOptions: SidebarDropdownOption[] = [
  { url: NotificationCategories.BannedUser, title: 'Banned User' }
];

type SidebarProps = {
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
};

export default function Sidebar({ setIsSidebarOpen, isSidebarOpen }: SidebarProps) {
  const { isAdmin, isSuperAdmin, isGroupAdmin, group } = useAuth();
  const canExtractData = useCanExtractData();
  const canModifyEmbargoKeyword = useCanModifyEmbargoKeyword();
  const canModifyUrlExceptions = useCanModifyUrlExceptions();
  const canModifySkuChecker = useCanModifySkuChecker();
  const canSeeBlockedUsers = useCanSeeBlockedUsers();

  const { data: documentationPageNames } = useGetDocumentationPageNamesQuery();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 960) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setIsSidebarOpen]);

  const documentationOptions = [
    ...(documentationPageNames ? documentationPageNames.map((page) => ({ url: page, title: page })) : []),
    ...documentationOptionsBase
  ];

  const notificationOptions = canSeeBlockedUsers
    ? generalNotificationOptions.concat(blockedUsersNotificationsOptions)
    : generalNotificationOptions;

  return (
    <Box
      display="flex"
      flexDirection="column"
      bgcolor="#222222"
      width={isSidebarOpen ? '205px' : '75px'}
      height="100vh"
      position="fixed"
      top="0"
      overflow="auto"
    >
      <Box
        display="flex"
        flexDirection="column"
        height="120px"
        justifyContent="center"
        alignItems="center"
        gap="10px"
        padding="20px 0"
      >
        <Link to="/">
          <img src={isSidebarOpen ? LogoLarge : LogoSmall} alt={'logo'} />
        </Link>
        {isSidebarOpen && <code style={{ color: '#B3B3B3' }}>{version}</code>}
      </Box>
      <List sx={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingRight: '8px' }}>
        {!isProd && (
          <SidebarLink to="/content-qa" Icon={DashboardIcon} text="Dashboard" isSidebarOpen={isSidebarOpen} />
        )}
        <SidebarLink to="/search" Icon={SingleReportIcon} text="Single Reports" isSidebarOpen={isSidebarOpen} />
        <SidebarLink to="/bulk" Icon={BulkTestIcon} text="Bulk Reports" isSidebarOpen={isSidebarOpen} />
        <SidebarDropdown
          text="Documentation"
          pathPrefix="/documentation"
          Icon={DocumentationIcon}
          directClickPath={documentationOptions[0].url}
          options={documentationOptions}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />

        <SidebarDropdown
          text="Notifications"
          pathPrefix="/notifications"
          Icon={BellIcon}
          directClickPath={notificationOptions[0].url}
          options={notificationOptions}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />

        {group?.runTask ? (
          <SidebarLink to="/automations/all" Icon={RobotIcon} text="Automation" isSidebarOpen={isSidebarOpen} />
        ) : null}

        {(isAdmin || isGroupAdmin) && <Divider />}
        {(isSuperAdmin ||
          canExtractData ||
          canModifyEmbargoKeyword ||
          canModifyUrlExceptions ||
          canModifySkuChecker) && (
          <SidebarLink
            to="/admin/dashboard"
            Icon={AdminDashboardIcon}
            text="Admin Dashboard"
            isSidebarOpen={isSidebarOpen}
          />
        )}

        {isSuperAdmin && group?.runTask ? (
          <SidebarLink
            to="/admin/taskStatistics"
            Icon={AdminDashboardIcon}
            text="Task Statistics"
            isSidebarOpen={isSidebarOpen}
          />
        ) : null}

        {isAdmin && (
          <SidebarLink
            to="/admin/users"
            Icon={UsersManagementIcon}
            text="Users management"
            isSidebarOpen={isSidebarOpen}
          />
        )}
        {!isProd && isSuperAdmin && (
          <SidebarLink to="/admin/billings" Icon={BillingsIcon} text="Billings" isSidebarOpen={isSidebarOpen} />
        )}
        {(isSuperAdmin || isGroupAdmin) && (
          <SidebarLink
            to="/admin/groups"
            Icon={GroupsManagmentIcon}
            text="Groups management"
            isSidebarOpen={isSidebarOpen}
          />
        )}
        {isSuperAdmin && (
          <>
            <SidebarLink to="/admin/health" Icon={HealthIcon} text="Health dashboard" isSidebarOpen={isSidebarOpen} />
            <SidebarLink to="/admin/logs" Icon={DocumentationIcon} text="Activity logs" isSidebarOpen={isSidebarOpen} />
          </>
        )}
      </List>

      <SidebarToggleButton isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />
    </Box>
  );
}

type SidebarLinkProps = {
  to: string;
  Icon: React.ComponentType;
  text: string;
  isSidebarOpen: boolean;
};

function SidebarLink({ to, Icon, text, isSidebarOpen }: SidebarLinkProps) {
  return (
    <ListItem sx={{ padding: 0 }}>
      <NavLink
        to={to}
        className={({ isActive }) =>
          ['sidebar__link', !isSidebarOpen ? 'sidebar__link--short' : '', isActive ? 'sidebar__link--active' : ''].join(
            ' '
          )
        }
      >
        <span className="link__icon">
          <Icon />
        </span>
        {isSidebarOpen && <Typography>{text}</Typography>}
      </NavLink>
    </ListItem>
  );
}

type DropdownButtonContentProps = {
  Icon: React.ComponentType;
  text: string;
  isDropdownOpen: boolean;
  isSidebarOpen: boolean;
};

function DropdownButtonContent({ Icon, text, isDropdownOpen, isSidebarOpen }: DropdownButtonContentProps) {
  return (
    <>
      <span className="link__icon">
        <Icon />
      </span>
      {isSidebarOpen ? (
        <>
          <Typography>{text}</Typography>
          <button className={['dropdown__arrow', isDropdownOpen ? 'dropdown__arrow--open' : ''].join(' ')}>
            <ArrowIcon />
          </button>
        </>
      ) : null}
    </>
  );
}

type SidebarDropdownOption = {
  url: string;
  title: string;
};

type SidebarDropdownProps = {
  text: string;
  pathPrefix: string;
  Icon: React.ComponentType;
  options: SidebarDropdownOption[];
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
  directClickPath?: string;
};

function SidebarDropdown({
  text,
  pathPrefix,
  options,
  Icon,
  isSidebarOpen,
  setIsSidebarOpen,
  directClickPath
}: SidebarDropdownProps) {
  const isActive = location.pathname.includes(pathPrefix);
  const [isDropdownOpen, setIsDropdownOpen] = useState(isActive);

  function toggleDropdown(event: React.SyntheticEvent) {
    event.stopPropagation();
    setIsDropdownOpen((prev) => !prev);

    if (!isSidebarOpen) {
      setIsSidebarOpen(true);
    }
  }

  return (
    <ListItem sx={{ padding: 0 }}>
      <Box display="flex" flexDirection="column" width="100%">
        {directClickPath ? (
          <NavLink
            className={[
              'sidebar__link sidebar__link_dropdown',
              !isSidebarOpen ? 'sidebar__link--short' : '',
              isActive ? 'sidebar__link--active' : ''
            ].join(' ')}
            to={`${pathPrefix}/${directClickPath}`}
            onClick={toggleDropdown}
          >
            {
              <DropdownButtonContent
                Icon={Icon}
                text={text}
                isDropdownOpen={isDropdownOpen}
                isSidebarOpen={isSidebarOpen}
              />
            }
          </NavLink>
        ) : (
          <>
            <button
              className={[
                'sidebar__link sidebar__link_dropdown',
                !isSidebarOpen ? 'sidebar__link--short' : '',
                isActive ? 'sidebar__link--active' : ''
              ].join(' ')}
              onClick={toggleDropdown}
            >
              <DropdownButtonContent
                Icon={Icon}
                text={text}
                isDropdownOpen={isDropdownOpen}
                isSidebarOpen={isSidebarOpen}
              />
            </button>
          </>
        )}
        {isDropdownOpen && isSidebarOpen && (
          <List>
            {options.map(({ url, title }) => (
              <NavLink
                key={url}
                to={`${pathPrefix}/${url}`}
                className={({ isActive }) => ['dropdown__option', isActive ? 'dropdown__option--active' : ''].join(' ')}
              >
                {title}
              </NavLink>
            ))}
          </List>
        )}
      </Box>
    </ListItem>
  );
}

type SidebarToggleButtonProps = {
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
};

function SidebarToggleButton({ isSidebarOpen, setIsSidebarOpen }: SidebarToggleButtonProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingRight: '8px', marginBottom: 3 }}>
      <button
        className={['sidebar__link', !isSidebarOpen ? 'sidebar__link--short' : ''].join(' ')}
        onClick={() => setIsSidebarOpen((prev) => !prev)}
      >
        <CollapseIcon style={!isSidebarOpen ? { transform: 'rotate(180deg)' } : {}} />
        {isSidebarOpen && <Typography>Collapse sidebar</Typography>}
      </button>
    </Box>
  );
}
